/** Pure helpers for the P&ID screen: words for states, progress, box scaling, the picker. */
import type { Me } from "../../lib/me";
import { serverTime } from "../../lib/when";
import { assetPath } from "../register/exports";
import type { ImportResult, Reading, RegisterTag, Sheet } from "./pidApi";

/** How the two readers compare on one reading, in words an engineer reads at a glance. */
export const AGREEMENT: Record<string, { label: string; tone: "ok" | "review" | "fail" | "info" }> = {
  agree: { label: "readers agree", tone: "ok" },
  disputed_confusable: { label: "look-alike characters", tone: "review" },
  second_reader_differs: { label: "readers differ", tone: "fail" },
  second_reader_blank: { label: "second reader saw nothing", tone: "review" },
  unreadable: { label: "unreadable", tone: "fail" },
  free_reader_only: { label: "free reader only", tone: "info" },
};

export function agreementOf(value: string) {
  return AGREEMENT[value] ?? { label: value.replace(/_/g, " "), tone: "info" as const };
}

export const REVIEW_TONE: Record<string, "ok" | "review" | "fail" | "info"> = {
  unreviewed: "info",
  accepted: "ok",
  edited: "ok",
  rejected: "fail",
};

/** A reading needs a person when nobody reviewed it and the readers did not plainly agree. */
export function needsLook(r: Reading): boolean {
  return r.review_state === "unreviewed" && r.agreement !== "agree";
}

export function isBusy(s: Sheet): boolean {
  return s.status === "queued" || s.status === "reading";
}

/** Progress of one sheet as a percent and a line of text. */
export function sheetProgress(s: Sheet): { pct: number; label: string } {
  if (s.status === "ready") return { pct: 100, label: `${s.readings} readings, ${s.reviewed} reviewed` };
  if (s.status === "failed") return { pct: 0, label: s.error ? `failed: ${s.error}` : "failed" };
  if (s.status === "queued") return { pct: 0, label: "waiting to be read" };
  const pct = s.tiles_total ? Math.round((100 * s.tiles_done) / s.tiles_total) : 0;
  return { pct, label: s.tiles_total ? `reading tile ${s.tiles_done} of ${s.tiles_total}` : "rendering the page" };
}

/** Why some tiles of a sheet were read by the free reader only, or null when none were. */
export function freeOnlyNote(s: Sheet): string | null {
  const notes = Object.entries(s.notes ?? {});
  if (!notes.length) return null;
  return notes.map(([why, n]) => `${n} of ${s.tiles_total} tiles: ${why}`).join("; ");
}

/** A box in 300 dpi page pixels, as CSS percentages of the shown page. */
export function boxStyle(box: number[], widthPx: number, heightPx: number) {
  const [x0, y0, x1, y1] = box;
  const pc = (v: number, of: number) => `${((100 * v) / of).toFixed(3)}%`;
  return { left: pc(x0, widthPx), top: pc(y0, heightPx), width: pc(x1 - x0, widthPx), height: pc(y1 - y0, heightPx) };
}

/** Values a person may accept without typing: the reading, the second reader's text, the alternates. */
export function acceptChoices(r: Reading): string[] {
  const all = [r.text, r.second_text, ...r.alternates].map((v) => (v ?? "").trim()).filter(Boolean);
  return [...new Set(all)];
}

/** Packages to offer: the person's scope, else the packages the documents area lists. */
export function packageChoices(me: Me | null | undefined, listed: string[]): string[] {
  const scoped = me?.packages ?? null;
  const names = scoped === null ? listed : scoped;
  return [...new Set(names)].sort();
}

/** Register rows an import would take: component kinds, reviewed cleanly. Mirrors the API's rule. */
export function importable(tags: RegisterTag[]): RegisterTag[] {
  return tags.filter((t) => ["instrument", "valve_number", "equipment"].includes(t.kind) && t.reviewed_status === "ok");
}

/** The query key for the open sheet. It carries the sheet's status and read count from the polled
 * list, so the drawing and its readings are fetched again when a read finishes or restarts. */
export function sheetDetailKey(sheetId: string | null, sheets: Pick<Sheet, "id" | "status" | "read_runs">[] | undefined) {
  const s = sheets?.find((x) => x.id === sheetId);
  return ["pid", "sheet", sheetId, s?.status ?? null, s?.read_runs ?? null] as const;
}

/** The API restarts a sheet left `reading` with no progress for this long (pid.STUCK_SECONDS). */
export const STUCK_MS = 10 * 60_000;

/** Whether to offer Read again: failed, queued, or a read that stopped making progress (for
 * example the API restarted mid-read). The server checks again and answers 409 if it is live. */
export function canReadAgain(s: Pick<Sheet, "status" | "updated_at">, now: number = Date.now()): boolean {
  if (s.status === "failed" || s.status === "queued") return true;
  return s.status === "reading" && now - (serverTime(s.updated_at)?.getTime() ?? now) >= STUCK_MS;
}

export function importSummary(result: ImportResult): string {
  const n = result.imported.length;
  const a = result.already.length;
  const refused = result.refused;
  if (!n && !a && !refused.length) {
    return "Nothing to import yet. Accept or edit readings of instrument, valve or equipment tags first.";
  }
  const parts = [];
  if (n) parts.push(`${n} tag${n === 1 ? "" : "s"} added to the asset register as candidates`);
  if (a) parts.push(`${a} already there`);
  for (const r of refused) parts.push(`${String(r.tag)} not imported: ${String(r.reason ?? "its register key belongs to another package")}`);
  return parts.join("; ") + ".";
}

/** The register entries an import made or found already there, each linked to its page. */
export function importedLinks(result: ImportResult): { tag: string; href: string; isNew: boolean }[] {
  const link = (isNew: boolean) => (x: { tag?: unknown }) => ({
    tag: String(x.tag), href: assetPath(result.package, String(x.tag)), isNew,
  });
  return [...result.imported.map(link(true)), ...result.already.map(link(false))];
}

/** Sheets grouped by the file they came from, in the order the API listed them. */
export function byFile(sheets: Sheet[]): { document_id: string; filename: string; sheets: Sheet[] }[] {
  const out: { document_id: string; filename: string; sheets: Sheet[] }[] = [];
  for (const s of sheets) {
    let group = out.find((g) => g.document_id === s.document_id);
    if (!group) {
      group = { document_id: s.document_id, filename: s.filename, sheets: [] };
      out.push(group);
    }
    group.sheets.push(s);
  }
  return out;
}
